<script setup lang="ts">
import { computed, inject, provide, ref } from "vue";
import { ExcursionsKey } from "@/components/RegistrationProcess/composables/useExcursions";
import type { Excursion } from "@/components/RegistrationProcess/types/events";
import { filterExcursionsByRules } from "@/util/excursionRules";
import StickyFooter from "../../StickyFooter.vue";
import ExcursionConfiguratorSkeleton from "../ExcursionConfiguratorSkeleton/index.vue";
import ExcursionIncludedSection from "../ExcursionIncludedSection/index.vue";
import ExcursionOptionalSection from "../ExcursionOptionalSection/index.vue";
import { ExcursionImageLoadingKey, type Props } from "./types.ts";

defineOptions({
  name: "ExcursionConfigurator",
});

const props = withDefaults(defineProps<Props>(), {
  title: "ExcursionConfigurator",
});

const { sessionExcursions, excursionRules, syncExcursionQueryParams, isExcursionIncluded } = inject(ExcursionsKey)!;

const { goToNextStep, goToPreviousStep } = inject<{ goToNextStep: () => void; goToPreviousStep: () => void }>(
  "steps",
)!;

const hasVisibleOptionalExcursion = ref(false);

const handleOptionalItemFullyVisible = () => {
  hasVisibleOptionalExcursion.value = true;
};

const loadedImageGroups = ref(0);

provide(ExcursionImageLoadingKey, {
  markImageGroupLoaded: () => {
    loadedImageGroups.value += 1;
  },
});

// Mirror the legacy (flag-off) screen: only excursions named by a selection rule are shown,
// so both flag states display an identical set before categorizing.
const visibleExcursions = computed<Excursion[]>(() =>
  filterExcursionsByRules(sessionExcursions.value ?? [], excursionRules.value),
);

const showSkeleton = computed<boolean>(() => {
  if (sessionExcursions.value === undefined) return true;
  const total = visibleExcursions.value.length;
  if (total === 0) return false;
  return loadedImageGroups.value < total;
});

const isDisabled = computed<boolean>(() => {
  const hasIncluded = includedExcursions.value.length > 0;
  const hasOptional = optionalExcursions.value.length > 0;

  if (hasOptional) {
    return !hasVisibleOptionalExcursion.value;
  }
  return !hasIncluded;
});

const includedExcursions = computed<Excursion[]>(() => visibleExcursions.value.filter((e) => isExcursionIncluded(e)));

const optionalExcursions = computed<Excursion[]>(() => visibleExcursions.value.filter((e) => !isExcursionIncluded(e)));

const saveAndContinue = async () => {
  await syncExcursionQueryParams();
  goToNextStep();
};
</script>

<template>
  <div class="flex flex-col w-full gap-8 p-6" data-testid="excursion-configurator">
    <ExcursionConfiguratorSkeleton v-show="showSkeleton" />
    <div :class="showSkeleton ? 'hidden' : 'contents'">
      <excursion-included-section v-if="includedExcursions.length > 0" v-model="includedExcursions" />
      <excursion-optional-section
        v-if="optionalExcursions.length > 0"
        v-model="optionalExcursions"
        :use-bold-title="includedExcursions.length === 0"
        @optional-item-fully-visible="handleOptionalItemFullyVisible"
      />
    </div>
  </div>
  <StickyFooter
      max-next-width="max-w-[260px]"
      :disabled-next="isDisabled"
      :show-required-section="false"
      @back="goToPreviousStep"
      @next="saveAndContinue"
    />
</template>
